import { useCallback, useEffect, useRef, useState } from 'react';

type DragOptions = { enableDrag: boolean; keepOldPosition: boolean };

// Todo: add support for touch devices (improvement)
export const useMouseDrag = (
  htmlElement: HTMLElement | null,
  options: DragOptions = {
    enableDrag: true,
    keepOldPosition: true,
  },
) => {
  const down = useRef(false);
  const dragPositionsRef = useRef({ x: 0, y: 0 });
  const downPosition = useRef<{ x: number; y: number } | null>(null);

  const element = htmlElement;
  const { enableDrag, keepOldPosition } = options;

  const [isDragging, setIsDragging] = useState(false);

  const setDragPositions = (x: number, y: number) => {
    dragPositionsRef.current.x = x;
    dragPositionsRef.current.y = y;
  };

  const setDownPositions = (x: number, y: number) => {
    downPosition.current = { x, y };
  };

  const resetDragPositions = useCallback(() => {
    if (!element) return;
    element.style.transform = '';

    if (!keepOldPosition) {
      setDragPositions(0, 0);
    }
  }, [element, keepOldPosition]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (element) {
        down.current = true;
        const dragPositions = dragPositionsRef.current;

        setDownPositions(e.pageX - dragPositions.x, e.pageY - dragPositions.y);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!element || !down.current || !downPosition.current) return;

      const { x, y } = downPosition.current;
      const dx = e.pageX - x;
      const dy = e.pageY - y;

      setIsDragging(true);
      setDragPositions(dx, dy);

      element.style.transform = `translate(${dx}px, ${dy}px)`;
    };

    const onMouseUp = (_e: MouseEvent) => {
      down.current = false;
      setIsDragging(false);
    };

    if (element && enableDrag) {
      const position = dragPositionsRef.current;
      if (keepOldPosition && position.x && position.y) {
        const { x, y } = position;
        element.style.transform = `translate(${x}px, ${y}px)`;
      }

      element.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove);
      element.addEventListener('mouseup', onMouseUp);
    }

    return () => {
      if (element) {
        element.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('mousemove', onMouseMove);
        element.removeEventListener('mouseup', onMouseUp);

        if (!keepOldPosition) {
          resetDragPositions();
        }
      }
    };
  }, [element, enableDrag, keepOldPosition, resetDragPositions]);

  return { isDragging, resetDragPositions };
};
